import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';

// Only the checks that failed: the template renders every entry, so a `false` would show up as the
// word "false" beside the real message.
function failures(errors: Record<string, string | false>): ValidationErrors | null {
  const failed = Object.entries(errors).filter(([, error]) => error);
  return failed.length ? Object.fromEntries(failed) : null;
}

function validateFeeRules(group: AbstractControl): ValidationErrors | null {
  const rules = (group.get('feeRules')?.value ?? []) as Checkout.FeePerItemRule[];

  return failures({
    missingAmount:
      rules.some((rule) => rule?.amount === null || rule?.amount === undefined || `${rule.amount}`.trim() === '') &&
      'Set the amount this fee charges per item.',
    rangeConflict:
      rules.some((rule, i) => {
        const prevRule = rules[i - 1];
        return prevRule && +prevRule.threshold >= +rule.threshold;
      }) &&
      `${
        rules[0]?.stepUnit === Checkout.FeeUnitType.ITEM ? 'Items' : 'Weight'
      } threshold must be greater than all previous rules threshold`,
  });
}

function validateGroupRules(group: AbstractControl): ValidationErrors | null {
  const rules = (group.get('groupRules')?.value ?? []) as Checkout.FeePerItemGroupRule[];
  const chargesUnmatched = Number(group.get('unmatchedFee')?.value?.amount) > 0;

  return failures({
    chargesNothing:
      !rules.length &&
      !chargesUnmatched &&
      'Add a rule, or charge items that match no rule, so this fee charges something.',
    missingValue: rules.some((rule) => !rule.value?.trim()) && 'Select a value before this rule can save.',
    invalidStep: rules.some((rule) => !(+rule.step >= 1)) && 'Every rule must charge once per at least one unit.',
    negativeAmount: rules.some((rule) => +rule.amount < 0) && 'A fee cannot be negative.',
    negativeFirstNFree:
      rules.some((rule) => rule.firstNFree !== undefined && +rule.firstNFree < 0) &&
      'Free items cannot be a negative number.',
  });
}

// On the group, so it re-runs on any child change. Rules kept for the inactive mode are never read
// here, which is what stops them blocking Save.
export const feePerItemValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  if (!group.get('enabled')?.value) {
    return null;
  }
  return group.get('byProductGroup')?.value ? validateGroupRules(group) : validateFeeRules(group);
};
